import { useTranslation } from "@sagot/i18n";
import { Button, ErrorState, Icon, Skeleton } from "@sagot/ui";
import { ADDON_PARAM } from "@sagot/constants";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { UpgradeCard, isPlanGateError, useBillingOverview, useFormatters, type BillingDetailsOut, type BillingOverview, type BillingPlan, type PlanKeyOut } from "../shared";
import { AddonDialog } from "./addon-dialog";
import { AddonsCard } from "./addons-card";
import { BillingDetailsCard } from "./billing-details-card";
import { BillingDetailsDialog } from "./billing-details-dialog";
import { launchTierOf, type BillingInterval } from "./billing-model";
import { CheckoutProvider } from "./checkout";
import { CurrentPlanCard } from "./current-plan-card";
import { useExportUsage } from "./export-usage";
import { InvoicesCard } from "./invoices-card";
import { PaymentMethodDialog } from "./payment-method-dialog";
import { InvoicedMethodsCard, PaymentMethodsCard } from "./payment-methods-card";
import { PlanChangeDialog, type PlanChangeTarget } from "./plan-change-dialog";
import { isManualBilling, ManualBillingNotice, ManualBillingProvider } from "./manual-billing";
import { PlansSection } from "./plans-section";
import { SalesRequestDialog, type SalesRequestStart } from "./sales-request-dialog";
import { SalesRequestsCard } from "./sales-requests-card";
import { ServiceUsageCard } from "./service-usage-card";
import { UsageBreakdownCard } from "./usage-breakdown-card";
import { UsageCard } from "./usage-card";
import { useBillingPlans, useCanManageBilling } from "./use-billing";

type AddonChoice = "extra_seat" | "message_pack" | "extra_agent" | "remove_branding" | "knowledge_storage_1gb" | "secure_launch_lite" | "secure_launch";
/** Add-ons a link may open directly (`?addon=` from the knowledge page's "Add storage"). */
const LINKABLE_ADDONS: readonly AddonChoice[] = ["knowledge_storage_1gb"];
type Overlay = { kind: "plan"; target: PlanChangeTarget } | { kind: "addon"; addon: AddonChoice } | { kind: "method" } | { kind: "sales"; start: SalesRequestStart } | { kind: "details"; details: BillingDetailsOut } | null;

function scrollToPlans() {
  document.getElementById("plans")?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
}

function BillingSkeleton() {
  return (
    <div className="space-y-6" aria-hidden="true">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Skeleton className="h-80" />
        <Skeleton className="h-80 xl:col-span-2" />
      </div>
      <Skeleton className="h-6 w-[120px]" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-80" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    </div>
  );
}

function Header({ overview }: { overview: BillingOverview | undefined }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const exportUsage = useExportUsage();
  const cycle = overview?.period_start && overview.period_end ? t("billing.cycleRange", { start: fmt.shortDate(overview.period_start), end: fmt.date(overview.period_end) }) : "";
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("billing.title")}</h1>
        <p className="mt-1 text-fg-muted">{t("billing.subtitle", { cycle })}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" loading={exportUsage.isPending} onClick={() => { exportUsage.mutate(); }}>
          <Icon name="download" />
          {t("billing.exportUsage")}
        </Button>
        <Button onClick={scrollToPlans}>
          <Icon name="arrow-up-right" />
          {t("billing.upgradePlan")}
        </Button>
      </div>
    </div>
  );
}

/** "Your billing currency changes to USD on Nov 10" while the current period runs out (DEC-042). */
function CurrencyChangeBanner({ overview }: { overview: BillingOverview }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { region } = overview;
  if (!region.effective_at || region.billing_currency === region.current_currency) return null;
  return (
    <p className="flex items-start gap-2 rounded-md border border-line bg-info-soft p-3 text-sm text-fg" role="status" data-testid="currency-change-banner">
      <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
      {t("billing.region.changeBanner", { currency: region.billing_currency, current: region.current_currency, date: fmt.date(region.effective_at) })}
    </p>
  );
}

/** `?addon=knowledge_storage_1gb` opens that purchase once (managers only); the parameter is then removed. */
function useLinkedAddon(canManage: boolean): AddonChoice | null {
  const [params, setParams] = useSearchParams();
  const requested = params.get(ADDON_PARAM);
  const [linked] = useState(() => LINKABLE_ADDONS.find((key) => key === requested) ?? null);
  useEffect(() => {
    if (requested === null) return;
    setParams((current) => { const next = new URLSearchParams(current); next.delete(ADDON_PARAM); return next; }, { replace: true });
  }, [requested, setParams]);
  return canManage ? linked : null;
}

function BillingContent({ overview, plans }: { overview: BillingOverview; plans: readonly BillingPlan[] }) {
  const { t } = useTranslation();
  const canManageBilling = useCanManageBilling();
  const manual = isManualBilling(overview);
  // A workspace billed by invoice has no self-serve plan, add-on or payment actions: they are gated (tooltip says why).
  const canManage = canManageBilling && !manual;
  const linked = useLinkedAddon(canManage);
  const [interval, setPeriod] = useState<BillingInterval>(overview.interval === "annual" ? "annual" : "monthly");
  const [overlay, setOverlay] = useState<Overlay>(linked ? { kind: "addon", addon: linked } : null);
  const close = () => { setOverlay(null); };
  const contactSales = (start: SalesRequestStart) => { setOverlay({ kind: "sales", start }); };
  const proPlan = plans.find((p) => !p.is_custom && (p.seats ?? 0) > (overview.plan.seats ?? 0));
  return (
    <ManualBillingProvider value={manual}>
      {manual ? <ManualBillingNotice overview={overview} onContactSales={() => { contactSales({ source: "pay_manually" }); }} /> : null}
      <CurrencyChangeBanner overview={overview} />
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <CurrentPlanCard
          overview={overview}
          plans={plans}
          canManage={canManage}
          onChangePlan={scrollToPlans}
          onSwitchAnnual={() => {
            const current = plans.find((p) => p.key === overview.plan.key) ?? overview.plan;
            setOverlay({ kind: "plan", target: { plan: current, interval: "annual" } });
          }}
        />
        <UsageCard overview={overview} canManage={canManage} onAddSeats={() => { setOverlay({ kind: "addon", addon: "extra_seat" }); }} onAddStorage={() => { setOverlay({ kind: "addon", addon: "knowledge_storage_1gb" }); }} />
      </section>
      <ServiceUsageCard />
      <PlansSection plans={plans} overview={overview} interval={interval} onIntervalChange={setPeriod} canManage={canManage} onSelect={(plan) => { setOverlay({ kind: "plan", target: { plan, interval } }); }} onContactSales={(plan) => { contactSales({ source: plan.is_custom ? "institution" : "plan", planKey: plan.key as PlanKeyOut, cycle: interval }); }} />
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {manual ? (
          <InvoicedMethodsCard onContactSales={() => { contactSales({ source: "pay_manually" }); }} />
        ) : (
          <PaymentMethodsCard methods={overview.payment_methods} offered={overview.offered_gateways} canManage={canManage} onAdd={() => { setOverlay({ kind: "method" }); }} onContactSales={() => { contactSales({ source: "pay_manually" }); }} />
        )}
        <BillingDetailsCard canManage={canManage} onEdit={(details) => { setOverlay({ kind: "details", details }); }} />
      </section>
      <AddonsCard overview={overview} canManage={canManage} onBuy={(addon) => { setOverlay({ kind: "addon", addon }); }} />
      <InvoicesCard canManage={canManage} gateways={overview.offered_gateways} />
      <SalesRequestsCard />
      <UsageBreakdownCard />
      {overlay?.kind === "plan" ? <PlanChangeDialog target={overlay.target} overview={overview} onClose={close} /> : null}
      {overlay?.kind === "addon" ? (
        <AddonDialog
          addon={overlay.addon}
          overview={overview}
          replacesLite={overlay.addon === "secure_launch" && launchTierOf(overview.features) === "lite"}
          upsell={overlay.addon === "extra_seat" && proPlan?.seats ? { label: t("billing.addons.seatsIncluded", { plan: proPlan.name, count: proPlan.seats }), onClick: () => { close(); scrollToPlans(); } } : undefined}
          onClose={close}
        />
      ) : null}
      {overlay?.kind === "method" ? <PaymentMethodDialog onClose={close} onContactSales={() => { contactSales({ source: "pay_manually" }); }} /> : null}
      {overlay?.kind === "sales" ? <SalesRequestDialog start={overlay.start} onClose={close} /> : null}
      {overlay?.kind === "details" ? <BillingDetailsDialog details={overlay.details} onClose={close} /> : null}
    </ManualBillingProvider>
  );
}

/** `/w/:ws/billing` (tenant/billing.html): plan, usage, plans, payments, receipts. */
export function BillingPage() {
  const { t } = useTranslation();
  const overview = useBillingOverview();
  const plans = useBillingPlans();
  const failed = overview.isError ? overview : plans.isError ? plans : null;
  return (
    <CheckoutProvider>
      <div className="mx-auto max-w-7xl space-y-6">
        <Header overview={overview.data} />
        {failed && isPlanGateError(failed.error) ? (
          <UpgradeCard title={t("billing.gate.title")} body={t("billing.gate.body")} icon="credit-card" />
        ) : failed ? (
          <div className="card">
            <ErrorState title={t("billing.loadError")} description={t("billing.loadErrorBody")} onRetry={() => void failed.refetch()} retryLabel={t("common.retry")} />
          </div>
        ) : overview.data && plans.data ? (
          <BillingContent overview={overview.data} plans={plans.data} />
        ) : (
          <BillingSkeleton />
        )}
      </div>
    </CheckoutProvider>
  );
}
